// src/pages/RoleManagement/modals/PermissionAssignModal.tsx
// Popup riêng để cập nhật quyền (module/permission + action-button/input theo trang) cho 1 role.
// Tách khỏi EditRoleModal theo yêu cầu: sửa thông tin role và cập nhật quyền là 2 luồng riêng biệt.

import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import ModuleCheckboxList from '../components/ModuleCheckboxList';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { clearPageActionInputs, clearAssignedModuleIds } from 'slices/roleManagement/reducer';
import {
  fetchRoleModules,
  assignRoleModulesForRole,
  fetchRolePageActionInputs,
  fetchPageActionInputsByUrlPage,
  assignRolePageActionInputsForRole,
} from 'slices/roleManagement/thunk';
import { RoleGroupDetail, Module, RolePageActionInputAssignment } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

interface PermissionAssignModalProps {
  isOpen: boolean;
  toggle: () => void;
  roleGroup: RoleGroupDetail | null;
  modules: Module[];
  onSuccess?: () => void;
}

const PermissionAssignModal: React.FC<PermissionAssignModalProps> = ({
  isOpen,
  toggle,
  roleGroup,
  modules,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { pageActionInputs, assignedPageActionInputs, assignedModuleIds, loading } = useSelector(
    (state: any) => state.RoleManagement
  );

  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);
  const [activeModule, setActiveModule] = useState<Module | null>(null);
  // Map<pageActionInputId, permissionId | null> — gộp lựa chọn của mọi module đã xem trong phiên popup này
  const [actionInputMap, setActionInputMap] = useState<Map<number, number | null>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  // Đánh dấu lần mở popup gần nhất đã reset cho role nào — dùng để phát hiện chuyển sang role khác
  // (hoặc mở lại popup) NGAY TRONG LÚC RENDER thay vì trong useEffect.
  const [resetForKey, setResetForKey] = useState<number | null>(null);

  const isSystemRole = roleGroup?.isProtected || roleGroup?.code === 'ADMIN';

  // Reset selectedModuleIds/activeModule ĐỒNG BỘ trong lúc render (không phải trong effect) ngay khi
  // phát hiện đổi sang role khác (hoặc mở lại popup) — bắt buộc phải đồng bộ vì ModuleCheckboxList bên
  // dưới remount qua key={roleGroup.id}: nếu reset chậm 1 nhịp (trong effect, effect của con chạy TRƯỚC
  // effect của cha), instance mới của ModuleCheckboxList sẽ nhận đúng 1 lần render đầu tiên với
  // selectedModuleIds CŨ của role trước đó, khiến effect tự-mở-rộng cây bên trong nó tính nhầm nhánh cần
  // mở theo role cũ — nhiễm vào expandedModuleIds và không tự dọn được khi dữ liệu role mới về là rỗng.
  const openKey = isOpen && roleGroup ? roleGroup.id : null;
  if (openKey !== null && openKey !== resetForKey) {
    setResetForKey(openKey);
    setSelectedModuleIds([]);
    setActiveModule(null);
  }

  // Side-effect thật (dispatch fetch/clear) vẫn phải nằm trong useEffect — dùng chung dependency với
  // logic reset đồng bộ ở trên để luôn chạy đúng 1 lần mỗi khi mở popup cho 1 role.
  useEffect(() => {
    if (isOpen && roleGroup) {
      dispatch(clearPageActionInputs());
      dispatch(clearAssignedModuleIds());
      dispatch(fetchRolePageActionInputs(roleGroup.id));
      dispatch(fetchRoleModules(roleGroup.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roleGroup?.id]);

  // Khi load xong danh sách Module đã gán cho role (RoleModuleMapping), set checked ban đầu
  useEffect(() => {
    setSelectedModuleIds(assignedModuleIds || []);
  }, [assignedModuleIds]);

  // Khi load xong danh sách PageActionInput đã gán cho role, set checked ban đầu để tối ưu UI
  useEffect(() => {
    const map = new Map<number, number | null>();
    (assignedPageActionInputs || []).forEach((item: RolePageActionInputAssignment) => {
      map.set(item.pageActionInputId, item.permissionId ?? null);
    });
    setActionInputMap(map);
  }, [assignedPageActionInputs]);

  const handleViewActions = (module: Module) => {
    // Bấm lại vào module đang mở thì thu gọn — giống hành vi expand/collapse của cây module
    if (activeModule?.id === module.id) {
      setActiveModule(null);
      dispatch(clearPageActionInputs());
      return;
    }

    setActiveModule(module);
    if (module.url) {
      dispatch(fetchPageActionInputsByUrlPage(module.url));
    } else {
      dispatch(clearPageActionInputs());
    }
  };

  const handleToggleActionInput = (id: number) => {
    setActionInputMap(prev => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, null);
      }
      return next;
    });
  };

  const handleClose = () => {
    setActiveModule(null);
    dispatch(clearPageActionInputs());
    toggle();
  };

  const handleSave = async () => {
    if (!roleGroup) return;

    // Role có thể đã được gán 1 module đã bị xóa (soft-delete) từ trước — module đó không còn trong
    // danh sách `modules` hiện hành nên không hiển thị trong cây để bỏ chọn, nhưng ID cũ vẫn còn nằm
    // trong selectedModuleIds (nạp từ assignedModuleIds lúc mở popup). Lọc lại theo `modules` trước
    // khi gửi để tránh lỗi "Module IDs không hợp lệ" từ BE khi trong đó có ID đã bị xóa.
    const validModuleIds = selectedModuleIds.filter((id) => modules.some((m) => m.id === id));

    setSubmitting(true);
    try {
      await dispatch(
        assignRoleModulesForRole({
          id: roleGroup.id,
          data: { moduleIds: validModuleIds },
        })
      ).unwrap();

      await dispatch(
        assignRolePageActionInputsForRole({
          id: roleGroup.id,
          data: {
            items: Array.from(actionInputMap.entries()).map(([pageActionInputId, permissionId]) => ({
              pageActionInputId,
              permissionId,
            })),
          },
        })
      ).unwrap();

      toast.success(st('cap_nhat_quyen_thanh_cong_message'));
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      toast.error(error || st('co_loi_xay_ra_khi_cap_message_3'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!roleGroup) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="xl">
      <ModalHeader toggle={handleClose}>
        {st('cap_nhat_permission_title_2')} {roleGroup.name}
      </ModalHeader>
      <ModalBody>
        {isSystemRole && (
          <Alert color="warning" className="mb-3">
            <i className="ri-alert-line me-2"></i>
            {st('role_he_thong_protected_khong_the_message_2')}
          </Alert>
        )}
        <div className="text-muted small mb-2">
          {st('nhan_icon_text')} <i className="ri-list-check-2"></i> {st('canh_1_module_de_mo_danh_text')}
        </div>
        <ModuleCheckboxList
          // key theo roleGroup.id — ép remount lại toàn bộ cây (reset state expand/collapse nội bộ)
          // mỗi khi đổi sang role khác, tránh cache trạng thái mở rộng của role trước đó lọt sang.
          key={roleGroup.id}
          modules={modules}
          selectedModuleIds={selectedModuleIds}
          onChange={setSelectedModuleIds}
          onViewActions={handleViewActions}
          activeModuleId={activeModule?.id}
          actionInputs={pageActionInputs}
          actionInputsLoading={loading}
          selectedActionInputIds={Array.from(actionInputMap.keys())}
          onToggleActionInput={handleToggleActionInput}
          disabled={isSystemRole}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose}>
          {st('huy_button')}
        </Button>
        {PageActionPermission.canDisplay(location.pathname, "button-permission-assign-submit") && (
          <Button
            color="primary"
            onClick={handleSave}
            disabled={submitting || isSystemRole}
            data-action-code="button-permission-assign-submit"
            data-action-name="Lưu quyền"
          >
            <i className="ri-save-line me-1"></i>
            {st('luu_quyen_button')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default PermissionAssignModal;
